import {
  AnalyticsEvents,
  ContentFulScreens,
  DiscoverCreateCharacterBlock,
  DiscoverTagCloud,
  type DiscoverTagCloudItem,
  useDiscoverChatbotProfileOverlayState,
} from "@next-platform/client-common-core";
import { useCallback, useMemo } from "react";

import PageMetaTags from "@/components/common/PageMetaTags";
import HeroBanner from "@/components/search/HeroBanner";
import { ROUTES } from "@/constants";
import useLocalizedNavigation from "@/hooks/useLocalizedNavigation";
import useQueueLogEvent from "@/hooks/useQueueLogEvent";
import DiscoverChatbotProfileOverlayHost from "@/pages/Discover/components/DiscoverChatbotProfileOverlayHost";
import DiscoverContentRow from "@/pages/Discover/components/DiscoverContentRow";
import DiscoverCreateCharacterBlockView from "@/pages/Discover/components/DiscoverCreateCharacterBlockView";
import DiscoverTagCloudView from "@/pages/Discover/components/DiscoverTagCloudView";
import DiscoverUnavailable from "@/pages/Discover/components/DiscoverUnavailable";
import hideDismissedDiscoverCharacters from "@/pages/Discover/hideDismissedDiscoverCharacters";
import useDiscoverLessLikeThis from "@/pages/Discover/hooks/useDiscoverLessLikeThis";
import useDiscoverNsfwSelection from "@/pages/Discover/hooks/useDiscoverNsfwSelection";
import useDiscoverPage from "@/pages/Discover/hooks/useDiscoverPage";

import useDiscoverRowVisibility from "./hooks/useDiscoverRowVisibility";

const Discover = () => {
  const viewModel = useDiscoverPage();
  const isNsfw = useDiscoverNsfwSelection();
  const { logEvent } = useQueueLogEvent();
  const localizedNavigate = useLocalizedNavigation();
  const overlay = useDiscoverChatbotProfileOverlayState();
  const getRowRef = useDiscoverRowVisibility({
    reportRowEligibility: viewModel.reportRowEligibility,
  });

  // Owned by the page so the cards and the overlay dismiss from the same list.
  const { dismissedCharacterIds, lessLikeThis } = useDiscoverLessLikeThis();
  const isPageUnavailable = viewModel.status === "unavailable" || viewModel.status === "error";

  const visibleRows = useMemo(() => {
    if (viewModel.status !== "ready") return [];

    return viewModel.rows
      .map((row) => hideDismissedDiscoverCharacters(row, dismissedCharacterIds))
      .filter((row): row is NonNullable<typeof row> => row != null)
      .filter((row) => row.status !== "empty");
  }, [dismissedCharacterIds, viewModel]);

  const handleTagCloudSelect = useCallback(
    (tagCloudItem: DiscoverTagCloudItem) => {
      logEvent(AnalyticsEvents.DISCOVERY_TAG_CLOUD_TAG_SELECT, {
        tag: tagCloudItem.tag,
        position: tagCloudItem.position,
      });
    },
    [logEvent],
  );

  const handleCreateCharacter = useCallback(() => {
    localizedNavigate(ROUTES.createChatbot);
  }, [localizedNavigate]);

  return (
    <div data-testid="Discover" className="flex min-w-0 w-full flex-col gap-8">
      <PageMetaTags />
      {isPageUnavailable ? (
        <DiscoverUnavailable />
      ) : (
        <>
          <HeroBanner screen={ContentFulScreens.HOME} />
          {viewModel.status === "loading" ? (
            <DiscoverContentRow row={undefined} onSelectCharacter={overlay.open} onLessLikeThis={lessLikeThis} />
          ) : (
            visibleRows.map((row) => (
              <div key={row.block.key} ref={getRowRef(row.block.key)} data-discover-row-key={row.block.key} className="min-w-0 w-full">
                <DiscoverContentRow row={row} onSelectCharacter={overlay.open} onLessLikeThis={lessLikeThis} />
              </div>
            ))
          )}

          <DiscoverTagCloud view={DiscoverTagCloudView} isNsfw={isNsfw} onTagSelect={handleTagCloudSelect} />
          <DiscoverCreateCharacterBlock view={DiscoverCreateCharacterBlockView} onCreateCharacter={handleCreateCharacter} />
        </>
      )}
      <DiscoverChatbotProfileOverlayHost selected={overlay.selected} onClose={overlay.close} onLessLikeThis={lessLikeThis} />
    </div>
  );
};

export default Discover;
